import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import FormDetails from 'app/components/form/FormDetails';
import FormBuilder from 'app/components/form/FormBuilder';

const useStyles = makeStyles(theme => ({
    input: {
        display: 'none',
    },
    listSubHeader: {
        backgroundColor: '#ffffff',
        color: theme.palette.error.main
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogPaper: {
        overflowY: 'initial'
    },
    height: {
        height: '100%'
    },
    content: {
        height: '100%',
        padding: '0'
    }
}));

const initForm = {
    group_id: 0,
    notification_group_id: 0,
    name: ``,
    description: ``,
    button_name: ``,
    active: false,
    entries_per_user: 1,
    starts_at: null,
    ends_at: null,
}

export default function NewFormDialog(props) {

    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();
    const history = useHistory();

    const initTime = DateTime.local().setZone(app.club.timezone).plus({days: 1}).startOf('hour');

    const { open, onClose, onClosed } = props;

    const [ groups, setGroups ] = useState(null);
    const [ errors, setErrors ] = useState({});
    const [ activeStep, setActiveStep ] = useState(0);
    const [ status, setStatus ] = useState('inactive');
    const [ loading, setLoading ] = useState(false);

    const [ form, setForm ] = useState({ ...initForm });
    const [ fields, setFields ] = useState([]);

    // Getters ---------------------------------------------------

    const getGroups = () => {
        setLoading(true);

        api.get('/admin/group')
            .then(res => setGroups(res.groups))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        getGroups();
    }, []);

    // Handlers ---------------------------------------------------

    const handleForm = name => event => {

        let value;

        if (DateTime.isDateTime(event) || event === null) {
            value = event;
        } else {
            const target = event.target;
            value = (target.type === 'checkbox' ? target.checked : target.value);
        }

        if (isError(name)) delete errors[name];

        setForm({ ...form, [name]: value });
    };

    const handleStatus = e => {

        const newStatus = e.target.value;
        const newForm = { ...form };

        switch (newStatus) {
            case 'inactive':
                newForm.active = false;
                newForm.starts_at = null;
                newForm.ends_at = null;
                newForm.global = false;
                break;
            case 'active':
                newForm.active = true;
                newForm.starts_at = null;
                newForm.ends_at = null;
                break;
            case 'scheduled':
                newForm.active = false;

                if (status !== 'scheduled' && form.starts_at === null && form.ends_at === null) {
                    newForm.starts_at = initTime;
                    newForm.ends_at = initTime.plus({ days: 7 });
                }

                break;
        }

        setStatus(newStatus);
        setForm(newForm);
    }

    const handleNext = () => {
        const nextStep = activeStep + 1;

        if (nextStep > 1) {
            postNewForm();
        }
        else {
            setActiveStep(nextStep);
        }
    }

    const handleBack = () => {
        const previousStep = activeStep - 1;
        if (previousStep < 0) {
            handleClose()
        }
        else setActiveStep(previousStep);
    }

    const getButtonText = () => {
        if (activeStep === 1) return 'Finish';
        return 'Next';
    }

    // Post Function ------------------------------------------

    const postNewForm = () => {

        setLoading(true)

        const data = {...form, fields}

        if (form.starts_at) data.starts_at = form.starts_at.toSeconds();
        if (form.ends_at) data.ends_at = form.ends_at.toSeconds();

        api.post('/admin/form/new', data)
            .then(res => history.push(`/admin/forms/${res.id}`))
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            })
            .finally(() => setLoading(false))
    }

    const handleClose = () => {
        onClose();
    }

    const handleClosed = () => {
        setForm({
            ...initForm,
            starts_at: null,
            ends_at: null
        });
        setFields([]);
        setActiveStep(0);

        if (onClosed) onClosed();
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            onExited={handleClosed}
            fullScreen
            aria-labelledby="new-form"
            classes={{paperScrollPaper: classes.dialogPaper}}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="form-builder">
                <span>Form Builder</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers className={classes.content}>
                { (activeStep === 0) &&
                    <FormDetails
                        form={form}
                        status={status}
                        groups={groups}
                        handleForm={handleForm}
                        handleStatus={handleStatus}
                        isError={isError}
                        getErrorMessage={getErrorMessage}
                        errors={errors}
                        newForm
                    />
                }
                { (activeStep === 1) &&
                    <FormBuilder
                        fields={fields}
                        setFields={setFields}
                        errors={errors}
                        newForm
                    />
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleBack}>
                    {activeStep === 0 ? 'Cancel' : 'Back'}
                </Button>
                <Button
                    onClick={handleNext}
                    variant="contained"
                    color='primary'
                    >
                    {getButtonText()}
                </Button>
           </DialogActions>
        </Dialog>
    )
}
